import { Divider, Stack } from '@ltvco/refresh-lib/theme';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import { useEffect, useState } from 'react';

import { CardContainer } from '../../components';
import {
  ChevronButton,
  TeaserAvatar,
  TeaserDetailLabel,
  TeaserDetailRow,
  TeaserDetailValue,
  TeaserInfo,
  TeaserItemContainer,
  TeaserItemRow,
  TeaserName,
} from '../components';

import {
  HeaderAddressBlock,
  HeaderGroup,
  HeaderRow,
  HeaderTitle,
  ShowMoreButton,
} from './components';

export interface PropertyOption {
  id: string;
  apn: string;
  owners: string[];
  landUse: string;
  parcelAddress: string;
}

export interface PropertyOptionsSearchParams {
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

interface PropertyOptionsPreviewCardProps {
  options: PropertyOption[];
  searchParams?: PropertyOptionsSearchParams;
  onOptionClick?: (option: PropertyOption) => void;
}

function formatAddress(params: PropertyOptionsSearchParams): string {
  const parts = [params.address, params.city, params.state].filter(Boolean);
  return parts.join(', ');
}

const PAGE_SIZE = 10;

const STACK_DIVIDER = <Divider />;

export const PropertyOptionsPreviewCard = ({
  options,
  searchParams,
  onOptionClick,
}: PropertyOptionsPreviewCardProps) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [options]);

  if (!options.length) {
    return null;
  }

  const addressLabel = searchParams ? formatAddress(searchParams) : '';
  const visibleOptions = options.slice(0, visibleCount);
  const hasMore = visibleCount < options.length;

  return (
    <CardContainer>
      <HeaderGroup>
        <HeaderRow>
          <HomeIcon sx={{ fontSize: 24, marginTop: '2px' }} />
          <HeaderAddressBlock>
            <HeaderTitle>{addressLabel || 'Property Report'}</HeaderTitle>
          </HeaderAddressBlock>
        </HeaderRow>
      </HeaderGroup>

      <Stack divider={STACK_DIVIDER}>
        {visibleOptions.map((option) => {
          const ownersLabel =
            option.owners.length > 0 ? option.owners.join(', ') : 'Unknown';

          return (
            <TeaserItemContainer
              key={option.id}
              onClick={() => onOptionClick?.(option)}
            >
              <TeaserItemRow>
                <TeaserAvatar>
                  <ApartmentIcon fontSize="small" />
                </TeaserAvatar>
                <TeaserInfo>
                  {option.parcelAddress && (
                    <TeaserName variant="body1">
                      {option.parcelAddress}
                    </TeaserName>
                  )}
                  <TeaserDetailRow>
                    <TeaserDetailLabel variant="body2">
                      Owners:
                    </TeaserDetailLabel>
                    <TeaserDetailValue variant="body2">
                      {ownersLabel}
                    </TeaserDetailValue>
                  </TeaserDetailRow>
                  <TeaserDetailRow>
                    <TeaserDetailLabel variant="body2">
                      Property Type:
                    </TeaserDetailLabel>
                    <TeaserDetailValue variant="body2">
                      {option.landUse || 'Unknown'}
                    </TeaserDetailValue>
                  </TeaserDetailRow>
                </TeaserInfo>
                <ChevronButton>
                  <ChevronRightIcon fontSize="small" />
                </ChevronButton>
              </TeaserItemRow>
            </TeaserItemContainer>
          );
        })}
      </Stack>

      {hasMore && (
        <ShowMoreButton onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
          Show more ({options.length - visibleCount} remaining)
        </ShowMoreButton>
      )}
    </CardContainer>
  );
};
